import React, { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import * as styles from './PlanRoomItem.module.scss'

import { PlanAvailableRoom } from '../../../apis/fvillage'
// NOTE: Import the enum *value* from the api file directly, not from the barrel
// — importing a runtime value through the barrel makes vite resolve the
// directory as a package at build time and fail with "Failed to resolve entry".
import { ImageResponseCategoryEnum } from '../../../apis/fvillage/api'
import {
  DEFAULT_TRANSACTION_ID,
  PATH,
  REGISTRATION_PATH,
} from '../../../constants'
import {
  useCreateNextPath,
  useImages,
  useOptions,
  usePropertyProvider,
  usePublication,
  useRegistrationState,
  useRoomFeature,
  useSearchQueryParams,
  useStorageEnabled,
} from '../../../hooks'
import i18n from '../../../i18n'
import { formatRoomSizeUnit, formatRoomType } from '../../../utils'
import {
  BaseText,
  Button,
  ButtonWrapper,
  Col,
  Container,
  Icon,
  Row,
  SlideShow,
  Text,
} from '../../basics'
import { TaxLabel } from '../../contents'
import { GalleryModal, RoomDetailModal } from '../../modals'

const translateNamespace = 'frames.PlanRoomIndexFrame'
const roomItemNamespace = 'components.RoomItem'
const extraNotificationNamespace = 'components.ExtraNotification'

type Props = {
  item: PlanAvailableRoom
  propertySlug: string
  planSlug: string
}

/**
 * Per-room card on the plan-first landing page (`/:property/:plan/rooms`).
 *
 * Mirrors the normal-flow `RoomItem` card (image slideshow + gallery/detail
 * modals + spec icon row + description + price block) so the plan-first page is
 * visually identical to the standard room list. Behaviour differs only where
 * the plan is fixed:
 *
 * - Price comes from `plan.price` (NOT `room.minPrice`).
 * - The CTA navigates straight to options / registrations (branching on
 *   `plan.withOptions`) with the fixed plan, skipping plan selection.
 * - The page is always priced (the backend defaults missing dates/counts, like
 *   the normal room list), so the card always shows the price + booking CTA —
 *   there is no "pick a date" gate.
 * - Cleanup + storage guard + keyboard-activation guard mirror `PlanItem`.
 */
export const PlanRoomItem: React.FC<Props> = ({
  item,
  propertySlug,
  planSlug,
}) => {
  const { t, i18n: i18nInstance } = useTranslation()
  const navigate = useNavigate()
  const property = usePropertyProvider()
  const { isHotel } = property
  const { onCreateNextPath } = useCreateNextPath()
  const { params } = useSearchQueryParams()
  const { numberOfAdults, numberOfChildren, numberOfInfants, roomCount } =
    params
  const { onClear: onClearOptions } = useOptions()
  const { onClearRegistrationStateByKey } = useRegistrationState()
  const storageEnabled = useStorageEnabled()

  const [isOpenDetailModal, toggleOpenDetailModal] = useState(false)
  const [isOpenGalleryModal, toggleGalleryModal] = useState(false)
  const [isOpenLayoutModal, toggleLayoutModal] = useState(false)
  const [galleryInitialSlide, setGalleryInitialSlide] = useState(0)

  const { room, plan } = item
  const roomPublication = usePublication(i18n.language, room.publications)
  const roomCategory = roomPublication?.name ?? ''
  const itemImages = useImages(room.images)
  const { canSmoke, petsAllowed, accessible } = useRoomFeature(
    room.feature ?? ''
  )
  const layoutImages = itemImages.filter(
    (image) => image.category === ImageResponseCategoryEnum.Layout
  )
  const hasLayoutImages = layoutImages.length > 0

  // `plan` is always present (the endpoint is always priced), but a room can
  // still be unavailable under `show_all_rooms=true`: `canReserve=false` or
  // `price === 0` ("stop selling") both mean not bookable.
  const price = plan.price ?? null
  const withOptions = plan.withOptions ?? true
  const isAvailable = !!plan.canReserve && (plan.price ?? 0) > 0

  // Mirror RoomItem's Simba-room badge: shown when the property has an
  // ExtraNotification entry and the room is the dedicated `fieldview` slug.
  const hasExtraNotification =
    !!propertySlug &&
    i18nInstance.exists(`${extraNotificationNamespace}.${propertySlug}.title`) &&
    i18nInstance.exists(`${extraNotificationNamespace}.${propertySlug}.body`)
  const isSimbaRoom = hasExtraNotification && room.slug === 'fieldview'

  const handleSelect = () => {
    // The shared `Button` only applies a `.disabled` class (pointer-events:
    // none) and does NOT set the native `disabled` attribute, so it stays
    // focusable and keyboard activation (Enter / Space) still fires onClick.
    // Re-check the same guard here so a visually-disabled CTA can't bypass the
    // availability / storage gate and navigate into options / registration.
    if (!isAvailable || !storageEnabled) {
      return
    }

    // Mirror PlanItem cleanup so options / registration draft from a
    // previous plan does not leak into this booking.
    onClearRegistrationStateByKey('specialServiceRequest')
    onClearRegistrationStateByKey('companyName')
    onClearRegistrationStateByKey('purposeOfUse')
    onClearRegistrationStateByKey('checkInTime')
    onClearRegistrationStateByKey('invoiceAddressee')
    onClearOptions()

    // Tag every navigation out of the plan-first room page with a
    // `from_plan_rooms` query param so the downstream Back buttons
    // (`OptionPlanIndex` and `RegistrationNewFrame`) return to THIS plan-first
    // room page (`/:property/:plan/rooms`) instead of falling through to the
    // public room-scoped plan list `PATH.plans`, which would leak the plan list
    // on a secret/plan-first URL (Codex P2 / 3359901212, 3361934674). A query
    // param (not router state) is used so the marker survives the login
    // redirect and page reloads that secret-plan flows commonly hit
    // (Codex P2 / 3360001710).
    if (withOptions) {
      navigate(
        onCreateNextPath(PATH.options, {
          queryParams: { ...params, fromPlanRooms: true },
          pathParams: {
            property: propertySlug,
            room: room.slug,
            plan: planSlug,
          },
        })
      )
      return
    }

    // No-option plan: skip the options page and go straight to registration.
    navigate(
      onCreateNextPath(REGISTRATION_PATH.registrations, {
        queryParams: {
          ...params,
          transaction: DEFAULT_TRANSACTION_ID,
          fromPlanRooms: true,
        },
        pathParams: {
          property: propertySlug,
          room: room.slug,
          plan: planSlug,
        },
      })
    )
  }

  return (
    <Fragment>
      {isOpenDetailModal && (
        <RoomDetailModal
          isOpen={isOpenDetailModal}
          images={room.images}
          onClose={() => toggleOpenDetailModal(false)}
          roomCategoryName={roomPublication?.name ?? ''}
          description={roomPublication?.description ?? ''}
          content={roomPublication?.descriptionDetail ?? ''}
          canSmoke={canSmoke}
          petsAllowed={petsAllowed}
          accessible={accessible}
          minPax={room.minPax}
          maxPax={room.maxPax}
          roomSize={room.roomSize}
          roomSizeUnit={room.roomSizeUnit}
          roomType={room.roomType}
          isHotel={isHotel ?? false}
          slug={room.slug}
          property={property.slug ?? ''}
        />
      )}
      {isOpenGalleryModal && (
        <GalleryModal
          isOpen={isOpenGalleryModal}
          onClose={() => toggleGalleryModal(false)}
          items={itemImages}
          initialSlide={galleryInitialSlide}
          renderItem={(image) => <img src={image.pc.src} alt={image.alt} />}
        />
      )}
      {isOpenLayoutModal && (
        <GalleryModal
          isOpen={isOpenLayoutModal}
          onClose={() => toggleLayoutModal(false)}
          items={layoutImages}
          initialSlide={0}
          renderItem={(image) => <img src={image.pc.src} alt={image.alt} />}
        />
      )}
      <Container>
        <Row gutterWidth={20} gutterHeight={10}>
          <Col pc={6}>
            <SlideShow
              items={itemImages}
              renderItem={(image) => (
                <figure>
                  <img
                    src={image.pc.src}
                    alt={image.alt}
                    className="pc"
                    width="600"
                    height="400"
                  />
                  <img
                    src={image.sp.src}
                    alt={image.alt}
                    className="sp"
                    width="600"
                    height="400"
                  />
                </figure>
              )}
              onTap={(swiper) => {
                setGalleryInitialSlide(swiper.activeIndex)
                toggleGalleryModal(true)
              }}
            />
          </Col>
          <Col pc={6}>
            <div className={styles.item} data-room-slug={room.slug}>
              <div
                className={styles.itemName}
                style={
                  isSimbaRoom
                    ? {
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '16px',
                      }
                    : undefined
                }
              >
                <div>
                  <h6>{roomCategory}</h6>
                  {isSimbaRoom && (
                    <Text size={12}>{t(`${roomItemNamespace}.simba`)}</Text>
                  )}
                </div>
                {isSimbaRoom && (
                  <img
                    src="/img/icon/icon-simba.svg"
                    alt="simba"
                    style={{
                      width: '100px',
                      height: '100px',
                      marginTop: '-32px',
                    }}
                    className="pc"
                  />
                )}
              </div>
              {isSimbaRoom && (
                <img
                  src="/img/icon/icon-simba.svg"
                  alt="simba"
                  style={{
                    width: '70px',
                    height: '70px',
                    marginTop: '-75px',
                    marginLeft: 'auto',
                  }}
                  className="sp"
                />
              )}
              {isHotel && (
                <div className={styles.itemInfo}>
                  <Icon mark="capacity">
                    {t(`${roomItemNamespace}.capacity`)}:{' '}
                    {room.minPax !== room.maxPax
                      ? t(`${roomItemNamespace}.availableGuestsRange`, {
                          min: room.minPax,
                          max: room.maxPax,
                        })
                      : t(`${roomItemNamespace}.availableGuests`, {
                          max: room.maxPax,
                        })}
                  </Icon>
                  <Icon mark="size">
                    {t(`${roomItemNamespace}.roomSize`)}:{' '}
                    {t(`${roomItemNamespace}.about`, { number: room.roomSize })}
                    {formatRoomSizeUnit(room.roomSizeUnit)}
                  </Icon>
                  <Icon mark="bed">
                    {t(`${roomItemNamespace}.roomType`)}:{' '}
                    {formatRoomType(room.roomType)}
                  </Icon>
                  {canSmoke ? (
                    <Icon mark="smoke">
                      {t(`${roomItemNamespace}.smokingAllowed`)}
                    </Icon>
                  ) : (
                    <Icon mark="smokeOff">
                      {t(`${roomItemNamespace}.nonSmoking`)}
                    </Icon>
                  )}
                  {petsAllowed && (
                    <Icon mark="dog">
                      {t(`${roomItemNamespace}.petsAllowed`)}
                    </Icon>
                  )}
                  {accessible && (
                    <Icon mark="wheelchair">
                      {t(`${roomItemNamespace}.accessible`)}
                    </Icon>
                  )}
                  {hasLayoutImages && (
                    <div className={styles.layoutButton}>
                      <Button
                        size="small"
                        htmlType="button"
                        variant="secondary"
                        display="block"
                        outline
                        onClick={() => toggleLayoutModal(true)}
                      >
                        {t(`${roomItemNamespace}.openLayout`)}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <div className={styles.itemPrice}>
                <div className={styles.roomsAndGuests}>
                  <BaseText color="gray3" size={12}>
                    {t(`${roomItemNamespace}.guests`, {
                      numberOfAdults,
                      numberOfChildren,
                      numberOfInfants,
                    })}
                    {isHotel &&
                      `・${t(`${roomItemNamespace}.rooms`, {
                        rooms: roomCount,
                      })}`}
                  </BaseText>
                </div>
                <div className={styles.price}>
                  {isAvailable && price !== null ? (
                    <Fragment>
                      {t(`${roomItemNamespace}.price`)}
                      <b className={styles.num}>{price.toLocaleString()}</b>
                      {t(`${roomItemNamespace}.yen`)}
                    </Fragment>
                  ) : (
                    // The page is always priced; a room that comes back
                    // unbookable for the searched dates is genuinely sold out,
                    // so show the "販売なし" label (parity with RoomItem).
                    <b className={styles.notAvailable}>
                      {t('components.ScheduleTable.notAvailable')}
                    </b>
                  )}
                </div>
                {/* On-site accommodation / bath tax notice, matching the
                    normal PlanItem price block (Codex P2 / 3359901213). */}
                {isAvailable && plan && (
                  <TaxLabel
                    collectAccommodationTaxOnSite={
                      plan.collectAccommodationTaxOnSite
                    }
                    collectBathTaxOnSite={plan.collectBathTaxOnSite}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className={styles.desc}>
              <p>{roomPublication?.description ?? ''}</p>
              <ButtonWrapper
                htmlType="button"
                outline
                size="smallWide"
                variant="secondary"
                position="center"
                onClick={() => toggleOpenDetailModal(true)}
              >
                {t(`${roomItemNamespace}.detail`)}
              </ButtonWrapper>
            </div>
            <div className={styles.button}>
              {/* Always show the booking CTA — the page is always priced, so
                  there is no "pick a date" prompt (parity with RoomItem). When
                  the room is sold out for the searched dates it falls back to
                  the disabled "予約不可" state. */}
              <ButtonWrapper
                htmlType="button"
                variant="primary"
                size="full"
                position="right"
                disabled={!isAvailable || !storageEnabled}
                onClick={handleSelect}
              >
                {isAvailable
                  ? t(`${translateNamespace}.selectRoomCta`)
                  : t(`${roomItemNamespace}.notAvailable`)}
              </ButtonWrapper>
            </div>
          </Col>
        </Row>
      </Container>
    </Fragment>
  )
}
